/* ═══════════════════════════════════════════════════════════════
 * buildPrivacyThemeMenu.ts
 *
 * Build the View-menu entries for the Privacy Suite theme switcher.
 *
 * The function is shared between the Privacy Suite host and the
 * standalone wizards so they all show the same toggle. It returns
 * an array of FLAT menu items because the WINBIT32 `MenuBar`
 * component only renders one level of submenus — a nested
 * `submenu` field on a `submenuitem` is silently dropped. To get
 * the theme switcher in front of the user, we splice the theme
 * options directly under `View` with a "Theme: " prefix so the
 * grouping is still obvious.
 *
 * Each entry uses the standard `submenuitem.checked` flag which
 * `MenuBar` already paints with a ✓ glyph (see the `submenuitemicon`
 * span in `MenuBar.jsx`), so the currently-active theme is
 * visibly ticked without any custom rendering.
 *
 * The action handlers are wired DIRECTLY to `setTheme` rather
 * than going through an action-string indirection — this is the
 * same pattern `ProgramManager.jsx` uses for the dynamic file-
 * menu entries and keeps each menu call-site a single line.
 * ═══════════════════════════════════════════════════════════════ */

import {
	PRIVACY_THEME_REGISTRY,
	type PrivacyThemeId,
} from '../theme/privacyThemeTokens';

/** Single, click-to-fire menu entry for one theme. */
export interface PrivacyThemeMenuEntry {
	readonly label: string;
	readonly fAction: () => void;
	/** Driven by `submenuitem.checked` (✓) in `MenuBar.jsx`. */
	readonly checked: boolean;
}

/**
 * Build the FLAT list of View-menu entries — one per registered
 * theme. The caller is expected to spread these directly into the
 * `View` submenu array; we deliberately don't wrap them in a
 * nested `submenu` because `MenuBar.jsx` only renders one level.
 *
 * The active theme gets the `checked: true` flag (which `MenuBar`
 * paints with a ✓) so the user can tell which one is live without
 * having to flip back and forth.
 */
export function buildPrivacyThemeMenuEntries(
	currentThemeId: PrivacyThemeId,
	setTheme: (id: PrivacyThemeId) => void,
): readonly PrivacyThemeMenuEntry[] {
	return Object.values(PRIVACY_THEME_REGISTRY).map((tokens) => ({
		// Prefix with "Theme: " so the user reads it as "Theme:
		// Classic" / "Theme: Phosphor" — keeps the grouping
		// obvious without depending on a nested-submenu UI we
		// don't have.
		label: `Theme: ${tokens.label}`,
		fAction: () => setTheme(tokens.id),
		checked: currentThemeId === tokens.id,
	}));
}
